import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSite } from '../data/SiteContext';
import {
  fetchHotel, fetchHotels, fetchHotelQuote, fmtDate, money, occupancyLine, partyLine, stars, stayLine,
} from '../data/publicHotels';
import StayPicker from '../components/StayPicker';

const Loading = ({ children }) => (
  <div className="wrap" style={{ paddingTop:80, paddingBottom:120, textAlign:'center', color:'#5a726f' }}>{children}</div>
);

export function HotelsList() {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState(null);
  const [error, setError] = useState(null);
  const [place, setPlace] = useState('All');

  useEffect(() => {
    fetchHotels().then(setHotels).catch(e => setError(e.message));
  }, []);

  if (error) return <Loading>Hotels are unavailable right now. Please try again shortly.</Loading>;
  if (!hotels) return <Loading>Loading hotels…</Loading>;
  if (!hotels.length) return <Loading>No hotels are on sale at the moment.</Loading>;

  const places = ['All', ...Array.from(new Set(hotels.map(h => h.cityName).filter(Boolean)))];
  const shown = place === 'All' ? hotels : hotels.filter(h => h.cityName === place);

  return (
    <div className="wrap" style={{ paddingTop:40, paddingBottom:72 }}>
      <div className="kicker">Where you'll stay</div>
      <h1 className="page-h1">Hotels & resorts</h1>
      <p className="page-sub" style={{ maxWidth:640 }}>
        Every property here is one we book directly, with live pricing. Prices are per room, from — tell us
        your dates and we'll confirm availability and quote the full stay.
      </p>

      {places.length > 2 && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:26 }}>
          {places.map(p => (
            <div key={p} onClick={() => setPlace(p)} className="card"
              style={{ padding:'9px 16px', borderRadius:100, fontWeight:700, fontSize:13.5, cursor:'pointer',
                color: place === p ? '#fff' : '#0c2b2f',
                background: place === p ? 'linear-gradient(135deg,#26c6a3,#0e8a9c)' : undefined }}>{p}</div>
          ))}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:22 }}>
        {shown.map(h => (
          <div key={h.id} className="card lift" onClick={() => navigate('/hotels/' + h.id)}
            style={{ cursor:'pointer', borderRadius:22, overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 12px 28px rgba(16,90,90,0.1)' }}>
            <div style={{ height:200, position:'relative' }}>
              <div className="bgimg" style={{ backgroundImage:`url(${h.img})` }}></div>
              {h.starCategory && (
                <div style={{ position:'absolute', top:12, left:12, padding:'5px 11px', borderRadius:100, background:'rgba(255,255,255,0.92)', color:'#a9761a', fontSize:12, fontWeight:800, letterSpacing:'.04em' }}>{stars(h.starCategory)}</div>
              )}
            </div>
            <div style={{ padding:20, display:'flex', flexDirection:'column', flex:1 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#0e9c86', textTransform:'uppercase', letterSpacing:'.05em' }}>
                {[h.cityName, h.countryName].filter(Boolean).join(' · ')}
              </div>
              <div className="serif" style={{ fontSize:21, margin:'6px 0 10px', lineHeight:1.2 }}>{h.name}</div>
              <div style={{ fontSize:14, color:'#5a726f', lineHeight:1.55, marginBottom:16, flex:1 }}>
                {(h.blurb || '').split('.').slice(0, 2).join('.') + (h.blurb ? '.' : '')}
              </div>
              <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:10 }}>
                <span><span style={{ fontSize:12.5, color:'#5a726f' }}>from </span><span style={{ fontWeight:800, fontSize:19 }}>{money(h.priceFrom)}</span></span>
                <span style={{ fontSize:13, fontWeight:700, color:'#0e8a9c' }}>View hotel →</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function HotelDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { contact } = useSite();
  const [h, setH] = useState(null);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const [stay, setStay] = useState({ stayFrom:'', stayTo:'', adults:2, childAges:[] });
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  useEffect(() => {
    setH(null); setError(null); setShowAll(false);
    setStay({ stayFrom:'', stayTo:'', adults:2, childAges:[] }); setQuote(null);
    fetchHotel(id).then(setH).catch(e => setError(e.message));
  }, [id]);

  // Price the stay once both dates are in. Changing a stepper re-asks, so the number always
  // belongs to what the picker currently shows.
  useEffect(() => {
    const { stayFrom, stayTo } = stay;
    if (!stayFrom || !stayTo || stayTo <= stayFrom) { setQuote(null); setQuoting(false); return; }

    let live = true;
    setQuoting(true);
    const t = setTimeout(() => {
      fetchHotelQuote(id, stay)
        .then(q => { if (live) setQuote(q); })
        .catch(e => { if (live) setQuote({ error: e.message || 'We could not price those dates.' }); })
        .finally(() => { if (live) setQuoting(false); });
    }, 250);

    return () => { live = false; clearTimeout(t); };
  }, [id, stay]);

  if (error) return <Loading>We couldn't find that hotel. <Link to="/hotels" style={{ color:'#0e8a9c', fontWeight:700 }}>See all hotels</Link></Loading>;
  if (!h) return <Loading>Loading…</Loading>;

  const quoted = quote && !quote.error && quote.priceFrom != null;
  const quotedRooms = quoted && quote.rooms?.length ? quote.rooms : null;
  const stayText = stay.stayFrom && stay.stayTo ? stayLine(stay.stayFrom, stay.stayTo) : null;
  const who = partyLine(stay.adults, stay.childAges);

  const list = quotedRooms || h.roomGrades;
  const grades = showAll ? list : list.slice(0, 6);

  const ask = quoted
    ? `Hi! I'd like to ask about ${h.name}${h.cityName ? ` in ${h.cityName}` : ''} for ${stayText}, ${who}. I saw it from ${money(quote.priceFrom)}.`
    : stayText
      ? `Hi! I'd like to ask about ${h.name}${h.cityName ? ` in ${h.cityName}` : ''} for ${stayText}, ${who}.`
      : `Hi! I'd like to ask about ${h.name}${h.cityName ? ` in ${h.cityName}` : ''}.`;
  const enquiry = `${contact.waLink}?text=${encodeURIComponent(ask)}`;

  // Nothing came back for that party: say which of the two reasons it was.
  const noPrice = quote && !quote.error && quote.priceFrom == null
    ? quote.shortestStay > 0
      ? `These rooms are sold with a minimum stay of ${quote.shortestStay} night${quote.shortestStay === 1 ? '' : 's'}. Lengthen the stay, or ask us.`
      : 'We do not hold a price for that party on those dates. Ask us and we will quote it.'
    : null;

  return (
    <div>
      <section style={{ position:'relative', minHeight:'clamp(340px,48vh,480px)', display:'flex', alignItems:'flex-end', overflow:'hidden' }}>
        <div className="cover"><div className="bgimg" style={{ backgroundImage:`url(${h.img})` }}></div></div>
        <div className="cover" style={{ background:'linear-gradient(180deg,rgba(6,40,45,0.15),rgba(6,40,45,0.75))' }}></div>
        <div style={{ position:'relative', maxWidth:1240, margin:'0 auto', padding:'0 24px 40px', width:'100%', color:'#fff' }}>
          <span onClick={() => navigate('/hotels')} style={{ cursor:'pointer', color:'rgba(255,255,255,0.9)', fontWeight:700, fontSize:14 }}>← All hotels</span>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', margin:'14px 0 6px' }}>
            {h.starCategory && <span style={{ padding:'5px 12px', borderRadius:100, background:'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.4)', fontSize:12.5, fontWeight:800 }}>{stars(h.starCategory)}</span>}
            <span style={{ fontSize:13.5, fontWeight:600, opacity:.92 }}>
              {[h.cityName, h.regionName, h.countryName].filter(Boolean).join(' · ')}
            </span>
          </div>
          <h1 className="serif" style={{ fontSize:'clamp(30px,5vw,54px)', margin:0, letterSpacing:'-0.5px', lineHeight:1.08 }}>{h.name}</h1>
        </div>
      </section>

      <div style={{ maxWidth:1240, margin:'0 auto', padding:'40px 24px 72px', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:40 }}>
        <div>
          {h.blurb && (
            <>
              <h2 className="serif" style={{ fontSize:28, margin:'0 0 12px' }}>About this hotel</h2>
              <p style={{ fontSize:16.5, lineHeight:1.7, color:'#0c2b2f' }}>{h.blurb}</p>
            </>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, margin:'24px 0 0' }}>
            {[
              h.maxAdults > 0 && ['Sleeps', `Up to ${h.maxAdults} adults`],
              ['Children', h.acceptsChildren ? 'Rates available' : 'Adults only'],
              h.mealPlans?.length && ['Board', h.mealPlans.join(', ')],
              h.minimumStay > 0 && ['Minimum stay', `${h.minimumStay} night${h.minimumStay === 1 ? '' : 's'}`],
            ].filter(Boolean).map(([label, value]) => (
              <div key={label} className="card" style={{ padding:'14px 16px', borderRadius:14 }}>
                <div style={{ fontSize:11.5, fontWeight:800, color:'#5a726f', textTransform:'uppercase', letterSpacing:'.06em' }}>{label}</div>
                <div style={{ fontWeight:700, fontSize:15, marginTop:4 }}>{value}</div>
              </div>
            ))}
          </div>

          {list.length > 0 && (
            <>
              <h3 className="serif" style={{ fontSize:23, margin:'34px 0 6px' }}>Rooms</h3>
              <p style={{ fontSize:14, color:'#5a726f', margin:'0 0 14px' }}>
                {quotedRooms
                  ? `${quotedRooms.length} room type${quotedRooms.length === 1 ? '' : 's'} priced for ${stayText}, ${who}.`
                  : `${h.roomGrades.length} room type${h.roomGrades.length === 1 ? '' : 's'}, priced from the lowest rate we currently hold.`}
              </p>
              <div style={{ display:'grid', gap:10 }}>
                {grades.map(g => (
                  <div key={g.code || g.name} className="card" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, padding:'15px 18px', borderRadius:14 }}>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:15.5 }}>{g.name}</div>
                      <div style={{ fontSize:12.5, color:'#5a726f', marginTop:3 }}>
                        {quotedRooms
                          ? (g.minimumStay > 0 ? `Minimum ${g.minimumStay} night${g.minimumStay === 1 ? '' : 's'}` : 'No minimum stay')
                          : `Up to ${g.maxAdults} adult${g.maxAdults === 1 ? '' : 's'}${g.acceptsChildren ? ' · children welcome' : ''}`}
                      </div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontSize:11.5, color:'#5a726f' }}>from</div>
                      <div style={{ fontWeight:800, fontSize:17 }}>{money(g.priceFrom)}</div>
                    </div>
                  </div>
                ))}
              </div>
              {list.length > 6 && (
                <div onClick={() => setShowAll(s => !s)} className="a-ghost"
                  style={{ display:'block', textAlign:'center', marginTop:12, padding:12, borderRadius:13, fontWeight:700, fontSize:14, cursor:'pointer', color:'#0e8a9c', background:'rgba(14,138,122,0.1)' }}>
                  {showAll ? 'Show fewer rooms' : `Show all ${list.length} room types`}
                </div>
              )}
            </>
          )}
        </div>

        <div>
          <div className="card" style={{ position:'sticky', top:100, padding:26, borderRadius:24, boxShadow:'0 16px 40px rgba(16,90,90,0.14)' }}>
            <div style={{ fontSize:13, color:'#5a726f', fontWeight:600 }}>{quoted ? 'Your stay, from' : 'Rooms from'}</div>
            <div className="serif" style={{ fontSize:44, color:'#0e8a9c', lineHeight:1, margin:'2px 0 2px', opacity: quoting ? 0.45 : 1 }}>
              {money(quoted ? quote.priceFrom : h.priceFrom)}
            </div>
            <div style={{ fontSize:13, color:'#5a726f', marginBottom:16 }}>
              {quoted ? `${stayText} · ${who}` : occupancyLine(h)}
            </div>

            <div style={{ paddingTop:16, borderTop:'1px solid rgba(14,138,122,0.12)' }}>
              <StayPicker
                maxAdults={h.maxAdults || 4}
                maxChildren={h.maxChildren || 0}
                oldestChildAge={h.oldestChildAge ?? 12}
                value={stay}
                onChange={setStay}
              />
            </div>

            {quoting && <div style={{ fontSize:13, color:'#5a726f', marginTop:12 }}>Checking those dates…</div>}
            {!quoting && quote?.error && (
              <div style={{ fontSize:13, color:'#c0492f', fontWeight:600, marginTop:12 }}>{quote.error}</div>
            )}
            {!quoting && noPrice && (
              <div style={{ fontSize:13, color:'#5a726f', marginTop:12, padding:'11px 13px', borderRadius:12, background:'rgba(169,118,26,0.1)' }}>{noPrice}</div>
            )}
            {!quoting && quoted && quote.isPriceFixed && (
              <div style={{ fontSize:13, color:'#5a726f', marginTop:12 }}>A flat rate for this hotel, whatever the dates.</div>
            )}

            {!stay.stayFrom && h.stayFrom && (
              <div style={{ fontSize:13, color:'#5a726f', marginTop:14 }}>
                Priced for stays between <b style={{ color:'#0c2b2f' }}>{fmtDate(h.stayFrom)}</b> and <b style={{ color:'#0c2b2f' }}>{fmtDate(h.stayTo)}</b>.
              </div>
            )}

            {h.destinationSlug && (
              <Link to={'/destinations/' + h.destinationSlug} style={{ display:'block', marginTop:14, fontSize:13.5, fontWeight:700, color:'#0e8a9c' }}>
                See the full {h.destinationName} trip →
              </Link>
            )}

            <a href={enquiry} target="_blank" rel="noreferrer" className="btn btn-green" style={{ display:'block', marginTop:18, padding:15, boxShadow:'0 10px 22px rgba(18,153,76,0.35)' }}>Check dates on WhatsApp</a>
            <a href={contact.telLink} style={{ display:'block', textAlign:'center', marginTop:10, padding:13, borderRadius:14, fontWeight:700, color:'#0e8a9c', background:'rgba(14,138,122,0.1)' }}>Or call {contact.phone}</a>
            <div style={{ fontSize:12, color:'#5a726f', marginTop:14, lineHeight:1.5 }}>
              Prices are per room and change with the season. They are a guide, not a held booking —
              we confirm the exact rate and availability for your dates before you pay anything.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
